import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronUp, Skull } from "lucide-react";
import { playSqueak, playKillSound, playSiren, getBassLevel } from "../lib/audio";
import { isAlly } from "../lib/theme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNT = window.innerWidth < 768 ? 4 : 7;
const BOSS_HP = 6;
const BOSS_BOUNTY = 25;

const makeRat = (id) => ({
  id,
  x: Math.random() * (window.innerWidth - 120),
  y: Math.random() * (window.innerHeight - 120),
  a: Math.random() * Math.PI * 2,
  s: 45 + Math.random() * 55,
  size: 46 + Math.random() * 30,
  zapped: false,
});

const getHunter = () => {
  let h = localStorage.getItem("rotu_hunter");
  if (!h) {
    h = "HUNTER-" + Math.random().toString(16).slice(2, 6).toUpperCase();
    localStorage.setItem("rotu_hunter", h);
  }
  return h;
};

export const RatSvg = ({ size }) => (
  <svg width={size} height={size * 0.62} viewBox="0 0 88 52" fill="none" className="rat-bob">
    <path
      d="M16 36 C 5 43 0 30 8 24 C 3 19 6 12 15 12"
      stroke="rgba(255,0,85,0.6)"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
    <path
      d="M26 15 L28 8 L31 14 M35 13 L37 6 L40 12 M44 12 L46 5 L49 11"
      stroke="rgba(255,0,85,0.5)"
      strokeWidth="1.6"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M14 36 C 12 26 22 15 38 14 C 54 13 64 21 70 29 L 74 32 L 69 33.5 C 67 38 60 40 50 40 L 24 40 C 17 40 15 38.5 14 36 Z"
      fill="#0A0A0F"
      stroke="rgba(57,255,20,0.45)"
      strokeWidth="1.5"
    />
    <circle cx="52" cy="14" r="6" fill="#0A0A0F" stroke="rgba(255,0,85,0.6)" strokeWidth="1.5" />
    <circle cx="52" cy="14" r="2.6" fill="rgba(255,0,85,0.35)" />
    <path
      d="M28 40 L26 44 M34 40 L33 44 M56 40 L54 44"
      stroke="rgba(255,0,85,0.7)"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <path d="M69 33 L70 37 L71.5 33.5 Z" fill="#E8E8EC" />
    <circle cx="60" cy="25" r="3.1" fill="#FF0022" className="rat-eye" />
    <circle cx="61" cy="24" r="1" fill="#FFD6DD" className="rat-eye" />
    <circle cx="73" cy="31" r="1.6" fill="#FF0055" className="rat-eye" />
  </svg>
);

const Crown = () => (
  <svg width="40" height="24" viewBox="0 0 34 20" className="absolute -top-6 left-1/2 -translate-x-1/2" data-testid="boss-crown">
    <path d="M2 18 L4 6 L11 12 L17 2 L23 12 L30 6 L32 18 Z" fill="#FFE600" stroke="#08080C" strokeWidth="1" />
  </svg>
);

const BottleSvg = () => (
  <svg width="110" height="156" viewBox="0 0 60 85" className="bottle-float" data-testid="boss-bottle">
    <rect x="21" y="2" width="18" height="10" rx="2" fill="#E50000" />
    <rect x="24" y="12" width="12" height="8" fill="#FFD800" />
    <rect x="12" y="20" width="36" height="61" rx="9" fill="#FFD800" stroke="#B967FF" strokeWidth="2.5" />
    <path d="M33 27 L24 47 L30 47 L27 61 L38 41 L31 41 Z" fill="#E50000" />
    <text
      x="30"
      y="73"
      textAnchor="middle"
      fill="#FFFFFF"
      stroke="#E50000"
      strokeWidth="2.5"
      paintOrder="stroke"
      fontSize="10"
      fontWeight="900"
      fontFamily="Arial, sans-serif"
    >
      RUSH
    </text>
    <text x="30" y="80" textAnchor="middle" fill="#E50000" fontSize="4.5" fontWeight="bold" fontFamily="Arial, sans-serif">
      JUNGLE JUICE
    </text>
  </svg>
);

export const RatLayer = () => {
  const rats = useRef(Array.from({ length: COUNT }, (_, i) => makeRat(i)));
  const nodes = useRef([]);
  const boss = useRef({ active: false, x: -400, y: -400, hp: BOSS_HP });
  const bossNode = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const lastEventAt = useRef(null);
  const allyRef = useRef(isAlly());
  const [ally, setAllyState] = useState(allyRef.current);
  const [sparks, setSparks] = useState([]);
  const [zapped, setZapped] = useState(null);
  const [board, setBoard] = useState([]);
  const [showBoard, setShowBoard] = useState(false);
  const [flash, setFlash] = useState(false);
  const [hunterId] = useState(getHunter);
  const [bossUi, setBossUi] = useState({ active: false, hp: BOSS_HP });
  const [feast, setFeast] = useState(null);
  const feastFx = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/music?slot=feast_fx`)
      .then((r) => {
        feastFx.current = r.data.track
          ? `${process.env.REACT_APP_BACKEND_URL}${r.data.track.url}`
          : null;
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onTheme = (e) => {
      allyRef.current = e.detail.ally;
      setAllyState(e.detail.ally);
    };
    window.addEventListener("rotu:theme", onTheme);
    return () => window.removeEventListener("rotu:theme", onTheme);
  }, []);

  const fetchBoard = () =>
    axios.get(`${API}/leaderboard`).then((res) => setBoard(res.data)).catch(() => {});

  const triggerFeast = (by) => {
    if (allyRef.current && feastFx.current) {
      const fx = new Audio(feastFx.current);
      window.dispatchEvent(new CustomEvent("rotu:music-pause"));
      const resume = () => window.dispatchEvent(new CustomEvent("rotu:music-resume"));
      fx.addEventListener("ended", resume);
      fx.addEventListener("error", resume);
      fx.play()
        .then(() => {
          window.__rotu_feast_fx = true;
        })
        .catch(() => {
          playSiren();
          resume();
        });
    } else {
      playSiren();
    }
    setFeast({
      by,
      drops: Array.from({ length: 60 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 1.8,
        dur: 2.4 + Math.random() * 2.4,
      })),
    });
    setTimeout(() => setFeast(null), 8000);
  };

  const checkEvent = () =>
    axios
      .get(`${API}/events/latest`)
      .then((res) => {
        const ev = res.data.event;
        if (!ev) return;
        if (lastEventAt.current === null) {
          lastEventAt.current = ev.at;
          return;
        }
        if (ev.at > lastEventAt.current) {
          lastEventAt.current = ev.at;
          triggerFeast(ev.display || ev.hunter);
        }
      })
      .catch(() => {});

  useEffect(() => {
    axios.get(`${API}/stats`).then((res) => setZapped(res.data.rats_zapped)).catch(() => setZapped(0));
    fetchBoard();
    checkEvent();
    const id = setInterval(() => {
      fetchBoard();
      checkEvent();
    }, 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, []);

  const spawnBoss = () => {
    boss.current.x = 100 + Math.random() * (window.innerWidth - 400);
    boss.current.y = 100 + Math.random() * (window.innerHeight - 350);
    boss.current.hp = BOSS_HP;
    boss.current.active = true;
    setBossUi({ active: true, hp: BOSS_HP });
    playSqueak();
  };

  useEffect(() => {
    const timer = setTimeout(spawnBoss, 20000);
    return () => clearTimeout(timer);
  }, []);

  const addSpark = (x, y, size) => {
    const spark = { id: Date.now() + Math.random(), x, y, size };
    setSparks((s) => [...s, spark]);
    setTimeout(() => setSparks((s) => s.filter((sp) => sp.id !== spark.id)), 650);
  };

  useEffect(() => {
    let last = performance.now();
    let raf;
    const step = (t) => {
      const dt = Math.min((t - last) / 1000, 0.05);
      last = t;
      const bass = getBassLevel();
      const speedMul = 1 + bass * 1.8;
      const w = window.innerWidth;
      const h = window.innerHeight;
      rats.current.forEach((r) => {
        if (r.zapped) return;
        if (Math.random() < 0.012) r.a += (Math.random() - 0.5) * 1.6;
        r.x += Math.cos(r.a) * r.s * dt * speedMul;
        r.y += Math.sin(r.a) * r.s * dt * speedMul;
        if (r.x < 0 || r.x > w - 90) {
          r.a = Math.PI - r.a;
          r.x = Math.max(0, Math.min(w - 90, r.x));
        }
        if (r.y < 0 || r.y > h - 60) {
          r.a = -r.a;
          r.y = Math.max(0, Math.min(h - 60, r.y));
        }
        const el = nodes.current[r.id];
        if (el) {
          el.style.transform = `translate3d(${r.x}px, ${r.y}px, 0)`;
          const inner = el.firstChild;
          if (inner) {
            inner.style.transform = `scaleX(${Math.cos(r.a) < 0 ? -1 : 1})`;
            if (!allyRef.current) {
              inner.style.filter =
                bass > 0.04
                  ? `hue-rotate(${Math.round(bass * -160)}deg) saturate(${(1 + bass).toFixed(2)})`
                  : "";
            } else {
              inner.style.filter = "";
            }
          }
        }
      });

      const b = boss.current;
      if (b.active) {
        if (allyRef.current) {
          b.x += Math.cos(t / 2600) * 70 * dt;
          b.y += Math.sin(t / 3400) * 50 * dt;
        } else {
          const dx = mouse.current.x - 110 - b.x;
          const dy = mouse.current.y - 60 - b.y;
          const dist = Math.hypot(dx, dy) || 1;
          const crawl = Math.min(30, dist * 0.25) * dt * (1 + bass * 1.2);
          b.x += (dx / dist) * crawl + Math.sin(t / 700) * 0.35;
          b.y += (dy / dist) * crawl + Math.cos(t / 900) * 0.35;
        }
        b.x = Math.max(0, Math.min(w - 250, b.x));
        b.y = Math.max(0, Math.min(h - 190, b.y));
        const el = bossNode.current;
        if (el) {
          el.style.transform = `translate3d(${b.x}px, ${b.y}px, 0)`;
          if (!allyRef.current) {
            const inner = el.querySelector(".boss-body");
            if (inner) inner.style.transform = `scaleX(${mouse.current.x - 110 - b.x < 0 ? -1 : 1})`;
          }
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const bloodFlash = () => {
    setFlash(true);
    setTimeout(() => setFlash(false), 500);
  };

  const zapRat = (r, e) => {
    if (r.zapped) return;
    r.zapped = true;
    playKillSound();
    playSqueak();
    const el = nodes.current[r.id];
    if (el) {
      el.style.transition = "opacity 0.15s";
      el.style.opacity = "0";
    }
    addSpark(e.clientX, e.clientY);
    bloodFlash();
    axios
      .post(`${API}/zap`, { hunter: hunterId })
      .then((res) => {
        setZapped(res.data.rats_zapped);
        fetchBoard();
      })
      .catch(() => setZapped((z) => (z ?? 0) + 1));
    setTimeout(() => {
      r.x = Math.random() < 0.5 ? -20 : window.innerWidth - 70;
      r.y = Math.random() * (window.innerHeight - 100);
      r.a = r.x < 0 ? 0 : Math.PI;
      r.zapped = false;
      if (el) {
        el.style.transition = "opacity 0.6s";
        el.style.opacity = "1";
      }
    }, 1400 + Math.random() * 1600);
  };

  const zapBoss = (e) => {
    const b = boss.current;
    if (!b.active) return;
    b.hp -= 1;
    playKillSound();
    addSpark(e.clientX, e.clientY, 100);
    if (b.hp <= 0) {
      b.active = false;
      setBossUi({ active: false, hp: 0 });
      addSpark(b.x + 110, b.y + 60, 190);
      bloodFlash();
      triggerFeast(hunterId);
      axios
        .post(`${API}/zap`, { hunter: hunterId, amount: BOSS_BOUNTY })
        .then((res) => {
          setZapped(res.data.rats_zapped);
          fetchBoard();
          return axios.get(`${API}/events/latest`);
        })
        .then((res) => {
          if (res.data.event) lastEventAt.current = res.data.event.at;
        })
        .catch(() => {});
      setTimeout(spawnBoss, 45000 + Math.random() * 25000);
    } else {
      setBossUi({ active: true, hp: b.hp });
      const el = bossNode.current;
      if (el) {
        el.style.opacity = "0.35";
        setTimeout(() => {
          if (bossNode.current) bossNode.current.style.opacity = "1";
        }, 130);
      }
    }
  };

  const hpColor = ally ? "#FF71CE" : "#FF0055";

  return (
    <>
      <div className="fixed inset-0 z-[5] pointer-events-none" data-testid="rat-layer">
        {rats.current.map((r) => (
          <div
            key={r.id}
            ref={(el) => (nodes.current[r.id] = el)}
            className="rat-wrap absolute top-0 left-0 pointer-events-auto cursor-crosshair"
            data-testid={`roaming-rat-${r.id}`}
            onPointerDown={(e) => zapRat(r, e)}
          >
            <div className={`transition-transform duration-150 ${ally ? "rat-rainbow" : ""}`}>
              <RatSvg size={r.size} />
            </div>
          </div>
        ))}
      </div>

      {bossUi.active && (
        <div
          ref={bossNode}
          className="fixed top-0 left-0 z-[6] cursor-crosshair transition-opacity duration-150"
          style={{ width: ally ? 140 : 240 }}
          data-testid="rat-king-boss"
          onPointerDown={zapBoss}
        >
          <div className="flex gap-1 mb-1 justify-center" data-testid="boss-hp">
            {Array.from({ length: BOSS_HP }).map((_, i) => (
              <span
                key={i}
                className="w-3 h-1.5"
                style={
                  i < bossUi.hp
                    ? { backgroundColor: hpColor, boxShadow: `0 0 6px ${hpColor}` }
                    : { backgroundColor: "#3f3f46" }
                }
              />
            ))}
          </div>
          <div className="boss-body relative transition-transform duration-200 flex justify-center">
            {ally ? (
              <BottleSvg />
            ) : (
              <>
                <Crown />
                <div style={{ filter: "saturate(1.7) drop-shadow(0 0 18px rgba(255,0,85,0.4))" }}>
                  <RatSvg size={230} />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {sparks.map((sp) => (
        <div
          key={sp.id}
          className="spark"
          style={{ left: sp.x, top: sp.y, ...(sp.size ? { width: sp.size, height: sp.size } : {}) }}
          data-testid="zap-spark"
        />
      ))}
      {flash && <div className="blood-flash" data-testid="blood-flash" />}

      {feast && (
        <>
          <div data-testid="blood-rain">
            {feast.drops.map((d) => (
              <span
                key={d.id}
                className="blood-drop"
                style={{
                  left: `${d.left}vw`,
                  animationDuration: `${d.dur}s`,
                  animationDelay: `${d.delay}s`,
                }}
              />
            ))}
          </div>
          <div className="fixed top-20 inset-x-0 z-[68] flex justify-center pointer-events-none px-4">
            <div
              className="border border-[#FF0055] bg-[#08080C]/90 backdrop-blur-xl px-8 py-4 text-center pulse-glow"
              data-testid="boss-feast-banner"
            >
              <div className="flex items-center gap-2 justify-center text-[10px] tracking-[0.35em] uppercase text-[#FF0055] mb-1">
                <Skull size={12} /> {ally ? "Jungle juice claimed" : "Rat king slain"}
              </div>
              <div className="font-display text-xl sm:text-2xl font-bold text-white glitchy">
                {feast.by} <span className="text-[#FFE600]">+{BOSS_BOUNTY} KILLS</span>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2">
        {showBoard && (
          <div
            className="w-52 sm:w-64 border border-[rgba(255,0,85,0.4)] bg-[#08080C]/95 backdrop-blur-xl"
            data-testid="leaderboard-panel"
          >
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#FF0055]">
              <Skull size={12} /> Top exterminators
            </div>
            <div className="max-h-56 overflow-y-auto">
              {board.length === 0 && (
                <div className="px-4 py-4 text-xs text-zinc-600">No confirmed kills yet.</div>
              )}
              {board.map((h, i) => (
                <div
                  key={h.id}
                  className={`flex items-center justify-between px-4 py-2.5 text-xs ${
                    h.id === hunterId ? "bg-[#39FF14]/10 text-[#39FF14]" : "text-zinc-400"
                  }`}
                  data-testid={`leaderboard-row-${i}`}
                >
                  <span className="flex items-center gap-2 truncate">
                    <span className="font-display text-[#FF0055] w-5 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {h.display || h.id}
                    {h.id === hunterId && <span className="text-[9px] uppercase">(you)</span>}
                  </span>
                  <span className="font-display tabular-nums shrink-0">{h.kills}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <button
          onClick={() => setShowBoard((s) => !s)}
          className="border border-[rgba(57,255,20,0.4)] bg-[#08080C]/85 backdrop-blur-xl px-2 py-1.5 sm:px-3 sm:py-2 text-left hover:border-[#39FF14] transition-colors duration-200"
          data-testid="zap-counter"
        >
          <div className="flex items-center justify-between gap-4">
            <span className="text-[8px] tracking-[0.3em] text-zinc-500 uppercase">
              Rats neutralized
            </span>
            {showBoard ? (
              <ChevronDown size={11} className="text-zinc-500" />
            ) : (
              <ChevronUp size={11} className="text-zinc-500" />
            )}
          </div>
          <div className="font-display text-base sm:text-lg font-bold text-[#39FF14] tabular-nums">
            {zapped === null ? "--" : String(zapped).padStart(4, "0")}
          </div>
          <div className="text-[8px] tracking-[0.25em] uppercase text-zinc-600 mt-0.5">
            You: {hunterId}
          </div>
        </button>
      </div>
    </>
  );
};
